import * as React from 'react';
import { DisplayDate } from 'owa-datetime';
import { AgendaEventViewBase, Detail, AgendaEventViewBaseProps } from 'owa-agenda-event-views';
import { TimeUntilView } from './AgendaEventTimeUntilView';
import { getDurationString } from '../selectors/userConfigSelectors';
import { formatUserTime } from '../selectors/dateTimeSelectors';
import { PersonaDetailView } from './PersonaDetailView';
import { observer } from 'mobx-react';
import { getIconFromProviderName } from '../utils/getIconFromProviderName';
import strings from 'owa-strings/owa-agenda-strings';
import { differenceInMinutes, now } from 'owa-datetime';

const styles = require('./AgendaEventViewUpNext.scss');
const classNames = require('classnames/bind').bind(styles);

export interface AgendaEventViewUpNextProps {
    startTime: DisplayDate;
    endTime: DisplayDate;
    organizerEmail: string;
    organizerName: string;
    location: string;
    subject: string;
    isCancelled: boolean;
    isMeeting: boolean;
    className: string;
    provider: string;
    joinUrl: string;
}

function withAgendaEventViewUpNextProps(
    AgendaEventViewBase: React.ComponentClass<AgendaEventViewBaseProps>
): React.ComponentType<
    Omit<AgendaEventViewBaseProps, 'details' | 'timeDetails' | 'buttonDetail'> &
        AgendaEventViewUpNextProps
> {
    const AgendaViewWithUpNext = observer(
        class extends React.Component<AgendaEventViewBaseProps & AgendaEventViewUpNextProps> {
            render() {
                const {
                    startTime,
                    endTime,
                    isCancelled,
                    subject,
                    location,
                    organizerEmail,
                    organizerName,
                    isMeeting,
                    className,
                    joinUrl,
                    provider,
                    ...passThroughProps
                } = this.props;
                const details: Detail[] = [];

                const colorScheme = passThroughProps.colorScheme;
                details.push({
                    getText: () => subject,
                    className: classNames(styles.subject, { isCancelled: isCancelled }),
                    colorScheme: colorScheme,
                });
                if (location) {
                    details.push({
                        getText: () => location,
                    });
                }
                if (isMeeting && organizerEmail) {
                    details.push({
                        renderCustomDetailPart: () => (
                            <PersonaDetailView
                                organizerEmail={organizerEmail}
                                organizerName={organizerName}
                            />
                        ),
                    });
                }
                let buttonDetail = null;
                if (joinUrl && differenceInMinutes(startTime, now()) < 15) {
                    buttonDetail = {
                        buttonProps: {
                            href: joinUrl,
                            iconProps: {
                                iconName: getIconFromProviderName(provider),
                                styles: { root: styles.providerIcon },
                            },
                            target: '_blank',
                            text: strings.agendaJoinButtonText,
                            onClick: this.onButtonClick,
                        },
                        colorScheme: colorScheme,
                    };
                }

                return (
                    <div className={styles.outerContainer}>
                        <TimeUntilView
                            startDate={startTime}
                            endDate={endTime}
                            containerClassName={styles.timeContainer}
                        />
                        <AgendaEventViewBase
                            {...passThroughProps}
                            timeDetails={[
                                {
                                    getText: () => formatUserTime(startTime),
                                    className: styles.timePrimaryText,
                                },
                                {
                                    getText: () => getDurationString(startTime, endTime),
                                    className: styles.timeSecondaryText,
                                },
                            ]}
                            details={details}
                            className={classNames(styles.container, className)}
                            buttonDetail={buttonDetail}
                        />
                    </div>
                );
            }

            private onButtonClick(evt: React.MouseEvent<EventTarget>): void {
                evt.stopPropagation();
            }
        }
    );

    return AgendaViewWithUpNext;
}

export const AgendaEventViewUpNext = withAgendaEventViewUpNextProps(AgendaEventViewBase);
